import { Fragment } from 'react';
import { Calendar, MapPin, Tag, ExternalLink, Ticket, Clock, Info } from 'lucide-react';
import { format, isToday, isTomorrow, isThisWeek } from 'date-fns';
import type { Event } from '../types/event';

function isSafeUrl(url: string | null | undefined): url is string {
  if (!url) return false;
  try {
    const { protocol } = new URL(url);
    return protocol === 'http:' || protocol === 'https:';
  } catch {
    return false;
  }
}

interface EventListProps {
  events: Event[];
  isLoading: boolean;
  onEventClick?: (event: Event) => void;
}

function formatEventDate(startStr: string | null, endStr: string | null): string {
  if (!startStr) return 'Date TBD';
  const start = new Date(startStr);
  const now = new Date();
  if (start < now && endStr) {
    const end = new Date(endStr);
    if (end > now) return `Ongoing through ${format(end, 'MMM d, yyyy')}`;
  }
  if (isToday(start)) return `Today, ${format(start, 'h:mm a')}`;
  if (isTomorrow(start)) return `Tomorrow, ${format(start, 'h:mm a')}`;
  if (isThisWeek(start)) return format(start, 'EEEE, h:mm a');
  return format(start, 'MMM d, yyyy · h:mm a');
}

function formatDistance(meters?: number): string | null {
  if (!meters && meters !== 0) return null;
  if (meters < 1000) return `${Math.round(meters)}m`;
  return `${(meters / 1609.344).toFixed(1)}mi`;
}

export default function EventList({ events, isLoading, onEventClick }: EventListProps) {
  if (isLoading) {
    return (
      <div className="flex flex-col flex-1 min-h-0 overflow-y-auto p-3 gap-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="p-3 rounded-xl bg-slate-50 animate-pulse">
            <div className="flex gap-3">
              <div className="w-14 h-14 rounded-lg bg-slate-200 flex-shrink-0" />
              <div className="flex-1 space-y-2 py-0.5">
                <div className="h-4 bg-slate-200 rounded-md w-3/4" />
                <div className="h-3 bg-slate-200 rounded-md w-1/2" />
                <div className="h-3 bg-slate-200 rounded-md w-2/3" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center px-8 py-16">
        <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
          <Calendar className="w-7 h-7 text-slate-300" />
        </div>
        <p className="text-slate-600 font-semibold text-sm">No events found</p>
        <p className="text-slate-400 text-xs mt-1.5 max-w-[200px] leading-relaxed">
          Try zooming out or searching a different area
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 min-h-0 overflow-y-auto">
      <div className="flex items-start gap-2 mx-2 mt-2 mb-1 px-2.5 py-2 bg-amber-50 border border-amber-100 rounded-lg">
        <Info className="w-3.5 h-3.5 text-amber-500 flex-shrink-0 mt-px" />
        <p className="text-[11px] text-amber-700 leading-relaxed">
          Times may vary — always confirm details on the event's website before heading out.
        </p>
      </div>
      <div className="flex flex-col p-2 gap-1">
      {events.map((event, index) => {
        const distance = formatDistance(event.distance_m);
        const hasLocation = event.latitude && event.longitude;
        const dateLabel = formatEventDate(event.start_datetime, event.end_datetime ?? null);
        const now = new Date();
        const startDate = event.start_datetime ? new Date(event.start_datetime) : null;
        const endDate = event.end_datetime ? new Date(event.end_datetime) : null;
        const isOngoing = startDate && startDate < now && endDate && endDate > now;
        const isUpcoming = !isOngoing && startDate && (isToday(startDate) || isTomorrow(startDate));

        return (
          <Fragment key={event.id}>
            <div
              className="group p-3 rounded-xl hover:bg-amber-50/60 transition-all cursor-pointer active:scale-[0.99]"
              onClick={() => onEventClick?.(event)}
            >
              <div className="flex gap-3">
                {event.image_url ? (
                  <img
                    src={event.image_url}
                    alt=""
                    className="w-14 h-14 rounded-lg object-cover flex-shrink-0 bg-slate-100"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                ) : (
                  <div className="w-14 h-14 rounded-lg bg-gradient-to-br from-amber-50 to-slate-100 flex-shrink-0 flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-amber-400" />
                  </div>
                )}

                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-slate-900 text-[13px] leading-snug line-clamp-2 group-hover:text-amber-700 transition-colors">
                    {event.title}
                  </h3>

                  <div className="flex items-center gap-1 mt-1">
                    <Clock className="w-3 h-3 text-slate-400 flex-shrink-0" />
                    <span className={`text-[11px] truncate ${isUpcoming ? 'text-amber-600 font-medium' : isOngoing ? 'text-emerald-600 font-medium' : 'text-slate-500'}`}>
                      {dateLabel}
                    </span>
                  </div>

                  {(event.venue_name || event.city) && (
                    <div className="flex items-center gap-1 mt-0.5">
                      <MapPin className="w-3 h-3 text-slate-400 flex-shrink-0" />
                      <span className="text-[11px] text-slate-500 truncate">
                        {event.venue_name || [event.city, event.state].filter(Boolean).join(', ')}
                      </span>
                      {distance && hasLocation && (
                        <span className="text-[11px] text-slate-400 flex-shrink-0">· {distance}</span>
                      )}
                    </div>
                  )}

                  <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                    {event.price && (
                      <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md">
                        {event.price}
                      </span>
                    )}
                    {event.categories?.slice(0, 2).map((cat, i) => (
                      <span key={i} className="flex items-center gap-0.5 text-[10px] font-medium text-slate-500 bg-slate-100 px-2 py-0.5 rounded-md">
                        <Tag className="w-2.5 h-2.5" />
                        {cat}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="flex flex-col items-center gap-2 flex-shrink-0 pt-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  {isSafeUrl(event.url) && (
                    <a
                      href={event.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="w-7 h-7 flex items-center justify-center rounded-md text-slate-400 hover:text-amber-600 hover:bg-amber-100 transition-all"
                      title="View event"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  )}
                  {isSafeUrl(event.ticket_url) && event.ticket_url !== event.url && (
                    <a
                      href={event.ticket_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="w-7 h-7 flex items-center justify-center rounded-md text-slate-400 hover:text-emerald-600 hover:bg-emerald-100 transition-all"
                      title="Get tickets"
                    >
                      <Ticket className="w-3.5 h-3.5" />
                    </a>
                  )}
                </div>
              </div>
            </div>
          </Fragment>
        );
      })}
      </div>
    </div>
  );
}
